import { type ComboboxItem, type MultiSelectProps as MantineMultiSelectProps } from '@mantine/core';
import { MultiSelectInput } from '../../inputs/MultiSelectInput';
import { useEditorContext } from '../EditorContext';
import { setEditorFieldValue, type LooseKeys } from '../types';
import { ModalEditorItemBase } from './ModalEditorItemBase';

export interface ModalMultiSelectItemProps<TFormType>
    extends Omit<MantineMultiSelectProps, 'data' | 'value' | 'onChange'> {
    fieldKey: LooseKeys<TFormType>;
    data: ComboboxItem[];
    required?: boolean;
    label?: string;
    labelWidth?: number;
}

export const ModalMultiSelectItem = <TFormType,>({
    fieldKey,
    data,
    required,
    label,
    labelWidth,
    ...multiSelectProps
}: ModalMultiSelectItemProps<TFormType>) => {
    const { form, loading, onSave } = useEditorContext<TFormType>();

    const fieldKeyString = fieldKey as string;
    const error = form.errors[fieldKeyString];

    // getInputProps resolves nested paths (e.g. `judges.0.elementGroups`); uncontrolled forms only expose defaultValue
    const inputProps = form.getInputProps(fieldKeyString);
    const currentValue: unknown = inputProps.value ?? inputProps.defaultValue;

    // Handle both array of IDs and array of objects
    const value = Array.isArray(currentValue)
        ? currentValue.map((item) => (typeof item === 'string' ? item : (item as { id: string }).id))
        : [];

    const handleChange = (values: string[]) => {
        setEditorFieldValue(form, fieldKeyString, values);
    };

    return (
        <ModalEditorItemBase
            label={label}
            labelWidth={labelWidth}
            error={error}
            required={required}
            submitChanges={onSave}
        >
            <MultiSelectInput
                key={form.key(fieldKeyString)}
                data={data}
                defaultValue={value}
                onChange={handleChange}
                readOnly={loading}
                error={false}
                {...multiSelectProps}
            />
        </ModalEditorItemBase>
    );
};
